import { useEffect, useRef } from 'react';
import { Badge } from '../types/index';
import { badgeModalService } from '../services/badgeModalService';
import { useProfileStore } from '../store/useProfileStore';

/**
 * Hook to detect newly earned badges by comparing current badges with previous state.
 * Skips detection on initial mount to avoid showing modals for badges already earned.
 * 
 * @param currentBadges - Array of badges from teamUserSummary.badges (only earned badges)
 */
export function useBadgeDetection(currentBadges: Badge[] | undefined) {
  const profile = useProfileStore((state) => state.activeProfile);
  const previousBadgeIds = useRef<Set<string>>(new Set());
  const isInitialized = useRef<boolean>(false);
  const testBadgeShown = useRef<boolean>(false);

  useEffect(() => {
    // Check if test badge mode is enabled
    const isTestMode = badgeModalService.getTestMode();
    
    if (isTestMode && !testBadgeShown.current) {
      // Test mode: inject test badge
      const testBadge = badgeModalService.getTestBadge();
      badgeModalService.show(testBadge, profile?.id);
      testBadgeShown.current = true;
      return;
    } else if (!isTestMode) {
      // Reset test badge flag when test mode is disabled
      testBadgeShown.current = false;
    }

    if (!currentBadges) {
      return;
    }

    const currentBadgeIds = new Set(currentBadges.map(b => b.id));

    if (!isInitialized.current) {
      // First load - just store IDs, don't show modals
      previousBadgeIds.current = currentBadgeIds;
      isInitialized.current = true;
      return;
    }

    // Find newly earned badges (badges that weren't in previous set)
    // Also check if badge has already been shown (metadata or in-memory cache)
    const newBadges = currentBadges.filter(
      badge => 
        !previousBadgeIds.current.has(badge.id) && 
        !badgeModalService.hasBadgeBeenShown(badge)
    );

    // Queue each new badge to show in modal
    newBadges.forEach(badge => {
      badgeModalService.show(badge, profile?.id);
    });

    // Update previous IDs for next comparison
    previousBadgeIds.current = currentBadgeIds;
  }, [currentBadges, profile?.id]);
}
